import type { TkaSideInput } from './tka';
// QME exam data model: types, normal-value tables, and pure init/update helpers.
// Extracted from App.tsx.

// AMA Guides 5th Edition Normal ROM Values
// These represent 0% impairment (full normal range of motion)
export const NORMAL_ROM_VALUES: Record<string, Record<string, number | string>> = {
  shoulders: {
    right_flexion: 180, left_flexion: 180,
    right_extension: 50, left_extension: 50,
    right_abduction: 180, left_abduction: 180,
    right_adduction: 50, left_adduction: 50,
    right_internal_rotation: 90, left_internal_rotation: 90,
    right_external_rotation: 90, left_external_rotation: 90,
  },
  elbows: {
    right_flexion: 140, left_flexion: 140,
    right_extension: 0, left_extension: 0,
    right_pronation: 80, left_pronation: 80,
    right_supination: 80, left_supination: 80,
  },
  wrists: {
    right_flexion: 60, left_flexion: 60,
    right_extension: 60, left_extension: 60,
    right_radial_deviation: 20, left_radial_deviation: 20,
    right_ulnar_deviation: 30, left_ulnar_deviation: 30,
  },
  hips: {
    right_flexion: 100, left_flexion: 100,
    right_extension_contracture: 0, left_extension_contracture: 0,
    right_internal_rotation: 40, left_internal_rotation: 40,
    right_external_rotation: 50, left_external_rotation: 50,
    right_adduction: 30, left_adduction: 30,
    right_abduction_contracture: 0, left_abduction_contracture: 0,
  },
  knees: {
    right_flexion: 140, left_flexion: 140,
    right_extension: 0, left_extension: 0,
    right_flexion_contracture_passive: 0, left_flexion_contracture_passive: 0,
    right_extensor_lag: 0, left_extensor_lag: 0,
    right_varus: 0, left_varus: 0,
    right_valgus: 0, left_valgus: 0,
  },
  ankles: {
    right_plantar_flexion: 50, left_plantar_flexion: 50,
    right_dorsiflexion: 20, left_dorsiflexion: 20,
    right_inversion: 30, left_inversion: 30,
    right_eversion: 20, left_eversion: 20,
    right_subtalar_inversion: 30, left_subtalar_inversion: 30,
    right_subtalar_eversion: 20, left_subtalar_eversion: 20,
  },
  thumbs: {
    right_ip_flexion: 80, left_ip_flexion: 80,
    // Extension is SIGNED (AMA Ch.16): a lag is positive, hyperextension negative.
    // Normal thumb IP sits in hyperextension, so the normal is -10, not +20 (+20
    // would read as a 20 degree extension lag = 3% digit impairment).
    right_ip_extension: -10, left_ip_extension: -10,
    right_mcp_flexion: 60, left_mcp_flexion: 60,
    right_mcp_extension: 0, left_mcp_extension: 0,
    right_cmc_radial_abduction: 50, left_cmc_radial_abduction: 50,
    right_cmc_adduction: 0, left_cmc_adduction: 0,
    right_cmc_opposition: 8, left_cmc_opposition: 8,
    right_radial_two_point: 5, left_radial_two_point: 5,
    right_radial_two_point_percent: 100, left_radial_two_point_percent: 100,
    right_ulnar_two_point: 5, left_ulnar_two_point: 5,
    right_ulnar_two_point_percent: 100, left_ulnar_two_point_percent: 100,
    right_amputation_percent: 0, left_amputation_percent: 0,
    right_triggering: 'None', left_triggering: 'None',
  },
  index_fingers: {
    right_dip_flexion: 70, left_dip_flexion: 70,
    right_dip_extension: 0, left_dip_extension: 0,
    right_pip_flexion: 100, left_pip_flexion: 100,
    right_pip_extension: 0, left_pip_extension: 0,
    right_mcp_flexion: 90, left_mcp_flexion: 90,
    // Signed extension: normal finger MCP is 20 degrees of HYPERextension = -20.
    right_mcp_extension: -20, left_mcp_extension: -20,
    right_radial_two_point: 5, left_radial_two_point: 5,
    right_radial_two_point_percent: 100, left_radial_two_point_percent: 100,
    right_ulnar_two_point: 5, left_ulnar_two_point: 5,
    right_ulnar_two_point_percent: 100, left_ulnar_two_point_percent: 100,
    right_amputation_percent: 0, left_amputation_percent: 0,
    right_triggering: 'None', left_triggering: 'None',
    right_extensor_subluxation_mcp: 'None', left_extensor_subluxation_mcp: 'None',
  },
  middle_fingers: {
    right_dip_flexion: 70, left_dip_flexion: 70,
    right_dip_extension: 0, left_dip_extension: 0,
    right_pip_flexion: 100, left_pip_flexion: 100,
    right_pip_extension: 0, left_pip_extension: 0,
    right_mcp_flexion: 90, left_mcp_flexion: 90,
    // Signed extension: normal finger MCP is 20 degrees of HYPERextension = -20.
    right_mcp_extension: -20, left_mcp_extension: -20,
    right_radial_two_point: 5, left_radial_two_point: 5,
    right_radial_two_point_percent: 100, left_radial_two_point_percent: 100,
    right_ulnar_two_point: 5, left_ulnar_two_point: 5,
    right_ulnar_two_point_percent: 100, left_ulnar_two_point_percent: 100,
    right_amputation_percent: 0, left_amputation_percent: 0,
    right_triggering: 'None', left_triggering: 'None',
    right_extensor_subluxation_mcp: 'None', left_extensor_subluxation_mcp: 'None',
  },
  ring_fingers: {
    right_dip_flexion: 70, left_dip_flexion: 70,
    right_dip_extension: 0, left_dip_extension: 0,
    right_pip_flexion: 100, left_pip_flexion: 100,
    right_pip_extension: 0, left_pip_extension: 0,
    right_mcp_flexion: 90, left_mcp_flexion: 90,
    // Signed extension: normal finger MCP is 20 degrees of HYPERextension = -20.
    right_mcp_extension: -20, left_mcp_extension: -20,
    right_radial_two_point: 5, left_radial_two_point: 5,
    right_radial_two_point_percent: 100, left_radial_two_point_percent: 100,
    right_ulnar_two_point: 5, left_ulnar_two_point: 5,
    right_ulnar_two_point_percent: 100, left_ulnar_two_point_percent: 100,
    right_amputation_percent: 0, left_amputation_percent: 0,
    right_triggering: 'None', left_triggering: 'None',
    right_extensor_subluxation_mcp: 'None', left_extensor_subluxation_mcp: 'None',
  },
  little_fingers: {
    right_dip_flexion: 70, left_dip_flexion: 70,
    right_dip_extension: 0, left_dip_extension: 0,
    right_pip_flexion: 100, left_pip_flexion: 100,
    right_pip_extension: 0, left_pip_extension: 0,
    right_mcp_flexion: 90, left_mcp_flexion: 90,
    // Signed extension: normal finger MCP is 20 degrees of HYPERextension = -20.
    right_mcp_extension: -20, left_mcp_extension: -20,
    right_radial_two_point: 5, left_radial_two_point: 5,
    right_radial_two_point_percent: 100, left_radial_two_point_percent: 100,
    right_ulnar_two_point: 5, left_ulnar_two_point: 5,
    right_ulnar_two_point_percent: 100, left_ulnar_two_point_percent: 100,
    right_amputation_percent: 0, left_amputation_percent: 0,
    right_triggering: 'None', left_triggering: 'None',
    right_extensor_subluxation_mcp: 'None', left_extensor_subluxation_mcp: 'None',
  },
};

// Function to ensure all body regions are initialized with default values
export const initializeExamData = (data: Partial<ExamData> | null): ExamData => {
  if (!data) return {} as ExamData;
  
  // Create a deep copy with all properties initialized to null
  
  return {
    shoulders: {
      right_flexion: null,
      left_flexion: null,
      right_extension: null,
      left_extension: null,
      right_abduction: null,
      left_abduction: null,
      right_adduction: null,
      left_adduction: null,
      right_internal_rotation: null,
      left_internal_rotation: null,
      right_external_rotation: null,
      left_external_rotation: null,
      right_includeAcromioplasty: false,
      left_includeAcromioplasty: false,
      ...data.shoulders
    },
    elbows: {
      right_flexion: null,
      left_flexion: null,
      right_extension: null,
      left_extension: null,
      right_pronation: null,
      left_pronation: null,
      right_supination: null,
      left_supination: null,
      ...data.elbows
    },
    hips: {
      right_flexion: null,
      left_flexion: null,
      right_extension_contracture: null,
      left_extension_contracture: null,
      right_internal_rotation: null,
      left_internal_rotation: null,
      right_external_rotation: null,
      left_external_rotation: null,
      right_adduction: null,
      left_adduction: null,
      right_abduction_contracture: null,
      left_abduction_contracture: null,
      // Total hip replacement, per hip (Table 17-34 point score; utils/jointReplacement.ts).
      // When tha_applies_<side> is true it replaces that hip's ROM rating.
      tha_applies_right: null,
      tha_applies_left: null,
      tha_right: null,
      tha_left: null,
      ...data.hips
    },
    wrists: {
      right_flexion: null,
      left_flexion: null,
      right_extension: null,
      left_extension: null,
      right_radial_deviation: null,
      left_radial_deviation: null,
      right_ulnar_deviation: null,
      left_ulnar_deviation: null,
      ...data.wrists
    },

    hands: {
      // Thumbs initialization
      thumbs: {
        // IP Joint
        right_ip_flexion: null,
        left_ip_flexion: null,
        right_ip_extension: null,
        left_ip_extension: null,
        // MCP Joint
        right_mcp_flexion: null,
        left_mcp_flexion: null,
        right_mcp_extension: null,
        left_mcp_extension: null,
        // CMC Joint
        right_cmc_radial_abduction: null,
        left_cmc_radial_abduction: null,
        right_cmc_adduction: null,
        left_cmc_adduction: null,
        right_cmc_opposition: null,
        left_cmc_opposition: null,
        // Sensory data
        right_radial_two_point: null,
        left_radial_two_point: null,
        right_radial_two_point_percent: null,
        left_radial_two_point_percent: null,
        right_ulnar_two_point: null,
        left_ulnar_two_point: null,
        right_ulnar_two_point_percent: null,
        left_ulnar_two_point_percent: null,
        // Other impairments
        right_amputation_percent: null,
        left_amputation_percent: null,
        right_triggering: null,
        left_triggering: null,
        ...((data as any)?.thumbs || data?.hands?.thumbs || {})
      },
      
      // Index fingers initialization
      index_fingers: {
        // DIP Joint
        right_dip_flexion: null,
        left_dip_flexion: null,
        right_dip_extension: null,
        left_dip_extension: null,
        // PIP Joint
        right_pip_flexion: null,
        left_pip_flexion: null,
        right_pip_extension: null,
        left_pip_extension: null,
        // MCP Joint
        right_mcp_flexion: null,
        left_mcp_flexion: null,
        right_mcp_extension: null,
        left_mcp_extension: null,
        // Sensory data
        right_radial_two_point: null,
        left_radial_two_point: null,
        right_radial_two_point_percent: null,
        left_radial_two_point_percent: null,
        right_ulnar_two_point: null,
        left_ulnar_two_point: null,
        right_ulnar_two_point_percent: null,
        left_ulnar_two_point_percent: null,
        // Other impairments
        right_amputation_percent: null,
        left_amputation_percent: null,
        right_triggering: null,
        left_triggering: null,
        right_extensor_subluxation_mcp: null,
        left_extensor_subluxation_mcp: null,
        ...((data as any)?.index_fingers || data?.hands?.index_fingers || {})
      },
      
      // Middle fingers initialization
      middle_fingers: {
        // DIP Joint
        right_dip_flexion: null,
        left_dip_flexion: null,
        right_dip_extension: null,
        left_dip_extension: null,
        // PIP Joint
        right_pip_flexion: null,
        left_pip_flexion: null,
        right_pip_extension: null,
        left_pip_extension: null,
        // MCP Joint
        right_mcp_flexion: null,
        left_mcp_flexion: null,
        right_mcp_extension: null,
        left_mcp_extension: null,
        // Sensory data
        right_radial_two_point: null,
        left_radial_two_point: null,
        right_radial_two_point_percent: null,
        left_radial_two_point_percent: null,
        right_ulnar_two_point: null,
        left_ulnar_two_point: null,
        right_ulnar_two_point_percent: null,
        left_ulnar_two_point_percent: null,
        // Other impairments
        right_amputation_percent: null,
        left_amputation_percent: null,
        right_triggering: null,
        left_triggering: null,
        right_extensor_subluxation_mcp: null,
        left_extensor_subluxation_mcp: null,
        ...((data as any)?.middle_fingers || data?.hands?.middle_fingers || {})
      },
      
      // Ring fingers initialization
      ring_fingers: {
        // DIP Joint
        right_dip_flexion: null,
        left_dip_flexion: null,
        right_dip_extension: null,
        left_dip_extension: null,
        // PIP Joint
        right_pip_flexion: null,
        left_pip_flexion: null,
        right_pip_extension: null,
        left_pip_extension: null,
        // MCP Joint
        right_mcp_flexion: null,
        left_mcp_flexion: null,
        right_mcp_extension: null,
        left_mcp_extension: null,
        // Sensory data
        right_radial_two_point: null,
        left_radial_two_point: null,
        right_radial_two_point_percent: null,
        left_radial_two_point_percent: null,
        right_ulnar_two_point: null,
        left_ulnar_two_point: null,
        right_ulnar_two_point_percent: null,
        left_ulnar_two_point_percent: null,
        // Other impairments
        right_amputation_percent: null,
        left_amputation_percent: null,
        right_triggering: null,
        left_triggering: null,
        right_extensor_subluxation_mcp: null,
        left_extensor_subluxation_mcp: null,
        ...((data as any)?.ring_fingers || data?.hands?.ring_fingers || {})
      },
      
      // Little fingers initialization
      little_fingers: {
        // DIP Joint
        right_dip_flexion: null,
        left_dip_flexion: null,
        right_dip_extension: null,
        left_dip_extension: null,
        // PIP Joint
        right_pip_flexion: null,
        left_pip_flexion: null,
        right_pip_extension: null,
        left_pip_extension: null,
        // MCP Joint
        right_mcp_flexion: null,
        left_mcp_flexion: null,
        right_mcp_extension: null,
        left_mcp_extension: null,
        // Sensory data
        right_radial_two_point: null,
        left_radial_two_point: null,
        right_radial_two_point_percent: null,
        left_radial_two_point_percent: null,
        right_ulnar_two_point: null,
        left_ulnar_two_point: null,
        right_ulnar_two_point_percent: null,
        left_ulnar_two_point_percent: null,
        // Other impairments
        right_amputation_percent: null,
        left_amputation_percent: null,
        right_triggering: null,
        left_triggering: null,
        right_extensor_subluxation_mcp: null,
        left_extensor_subluxation_mcp: null,
        ...((data as any)?.small_fingers || (data as any)?.little_fingers || data?.hands?.little_fingers || {})
      },
      ...data?.hands
    },

    knees: {
      right_flexion: null,
      left_flexion: null,
      right_extension: null,
      left_extension: null,
      right_flexion_contracture_passive: null,
      left_flexion_contracture_passive: null,
      right_extensor_lag: null,
      left_extensor_lag: null,
      right_varus: null,
      left_varus: null,
      right_valgus: null,
      left_valgus: null,
      // Total knee replacement, per knee (AMA Table 17-35 point score; see utils/tka.ts).
      // When tka_applies_<side> is true it replaces that knee's ROM rating.
      tka_applies_right: null,
      tka_applies_left: null,
      tka_right: null,
      tka_left: null,
      ...data?.knees
    },

    ankles: {
      right_plantar_flexion: null,
      left_plantar_flexion: null,
      right_dorsiflexion: null,
      left_dorsiflexion: null,
      right_inversion: null,
      left_inversion: null,
      right_eversion: null,
      left_eversion: null,
      right_subtalar_inversion: null,
      left_subtalar_inversion: null,
      right_subtalar_eversion: null,
      left_subtalar_eversion: null,
      ...data?.ankles
    },
    gait: {
      description: null,
      antalgic_gait: null,
      ...data.gait
    },
    grip_strength: {
      limited_by: null,
      right_attempt_1: null,
      left_attempt_1: null,
      right_attempt_2: null,
      left_attempt_2: null,
      right_attempt_3: null,
      left_attempt_3: null,
      ...data.grip_strength
    }
  };
};

// Helper function for safely updating any field in the exam data
export const updateExamField = (prev: ExamData | null, region: keyof ExamData | string[], field: string, value: any): ExamData => {
  if (!prev) return initializeExamData(null); // Initialize with empty data if prev is null
  
  const initialized = initializeExamData(prev); // Ensure all regions are initialized
  
  // Handle array path for nested properties (e.g., ['thumbs', 'active_range_of_motion', 'right'])
  if (Array.isArray(region)) {
    // Create a deep copy to avoid mutating the original object
    const result = {...initialized};
    
    // Navigate to the nested object
    let current: any = result;
    const pathLength = region.length;
    
    for (let i = 0; i < pathLength - 1; i++) {
      const key = region[i];
      // Ensure the path exists at each level
      if (!current[key]) {
        current[key] = {};
      }
      current = current[key];
    }
    
    // Set the field on the last nested object
    const lastKey = region[pathLength - 1];
    current[lastKey] = {
      ...current[lastKey],
      [field]: value
    };
    
    return result;
  }
  
  // Original implementation for backward compatibility
  const regionKey = region as keyof ExamData;
  const currentValue = initialized[regionKey];
  
  // Check if we're dealing with a primitive value or an object
  if (typeof currentValue !== 'object' || currentValue === null) {
    // If it's a primitive value, just set it directly
    return {
      ...initialized,
      [regionKey]: value
    };
  } else {
    // If it's an object, we can spread it
    return {
      ...initialized,
      [regionKey]: {
        ...currentValue,
        [field]: value
      }
    };
  }
};


/**
 * EXAM DATA TYPE DEFINITION
 * ======================
 * 
 * This type defines the structure for all exam data processed by the application.
 * When adding a new body region, follow these guidelines:
 * 
 * 1. Create a new section with nested object structure
 * 2. Use consistent naming patterns for all measurements (side_motion)
 * 3. Use number | null type for measurements to allow for unset values
 * 4. Use boolean | null for flags that can be toggled
 * 5. Document any special fields with comments
 * 6. Ensure all field names exactly match the backend JSON schema
 */
export type ExamData = {
  shoulders: {
    right_flexion: number | null;
    left_flexion: number | null;
    right_extension: number | null;
    left_extension: number | null;
    right_abduction: number | null;
    left_abduction: number | null;
    right_adduction: number | null;
    left_adduction: number | null;
    right_internal_rotation: number | null;
    left_internal_rotation: number | null;
    right_external_rotation: number | null;
    left_external_rotation: number | null;
    // Acromioplasty flags for shoulder ratings
    right_includeAcromioplasty: boolean | null;
    left_includeAcromioplasty: boolean | null;
  };
  elbows: {
    right_flexion: number | null;
    left_flexion: number | null;
    right_extension: number | null;
    left_extension: number | null;
    right_pronation: number | null;
    left_pronation: number | null;
    right_supination: number | null;
    left_supination: number | null;
  };
  hips: {
    right_flexion: number | null;
    left_flexion: number | null;
    right_extension_contracture: number | null;
    left_extension_contracture: number | null;
    right_internal_rotation: number | null;
    left_internal_rotation: number | null;
    right_external_rotation: number | null;
    left_external_rotation: number | null;
    right_adduction: number | null;
    left_adduction: number | null;
    right_abduction_contracture: number | null;
    left_abduction_contracture: number | null;
    tha_applies_right: boolean | null;
    tha_applies_left: boolean | null;
    tha_right: Record<string, any> | null;
    tha_left: Record<string, any> | null;
  };
  wrists: {
    right_flexion: number | null;
    left_flexion: number | null;
    right_extension: number | null;
    left_extension: number | null;
    right_radial_deviation: number | null;
    left_radial_deviation: number | null;
    right_ulnar_deviation: number | null;
    left_ulnar_deviation: number | null;
  };

  hands: {
    // Thumbs data structure
    thumbs: {
      // IP Joint
      right_ip_flexion: number | null;
      left_ip_flexion: number | null;
      right_ip_extension: number | null;
      left_ip_extension: number | null;
      // MCP Joint
      right_mcp_flexion: number | null;
      left_mcp_flexion: number | null;
      right_mcp_extension: number | null;
      left_mcp_extension: number | null;
      // CMC Joint
      right_cmc_radial_abduction: number | null;
      left_cmc_radial_abduction: number | null;
      right_cmc_adduction: number | null;
      left_cmc_adduction: number | null;
      right_cmc_opposition: number | null;
      left_cmc_opposition: number | null;
      // Sensory data
      right_radial_two_point: number | null;
      left_radial_two_point: number | null;
      right_radial_two_point_percent: number | null;
      left_radial_two_point_percent: number | null;
      right_ulnar_two_point: number | null;
      left_ulnar_two_point: number | null;
      right_ulnar_two_point_percent: number | null;
      left_ulnar_two_point_percent: number | null;
      // Other impairments
      right_amputation_percent: number | null;
      left_amputation_percent: number | null;
      right_triggering: string | null;
      left_triggering: string | null;
    };
    
    // Index fingers data structure
    index_fingers: {
      // DIP Joint
      right_dip_flexion: number | null;
      left_dip_flexion: number | null;
      right_dip_extension: number | null;
      left_dip_extension: number | null;
      // PIP Joint
      right_pip_flexion: number | null;
      left_pip_flexion: number | null;
      right_pip_extension: number | null;
      left_pip_extension: number | null;
      // MCP Joint
      right_mcp_flexion: number | null;
      left_mcp_flexion: number | null;
      right_mcp_extension: number | null;
      left_mcp_extension: number | null;
      // Sensory data
      right_radial_two_point: number | null;
      left_radial_two_point: number | null;
      right_radial_two_point_percent: number | null;
      left_radial_two_point_percent: number | null;
      right_ulnar_two_point: number | null;
      left_ulnar_two_point: number | null;
      right_ulnar_two_point_percent: number | null;
      left_ulnar_two_point_percent: number | null;
      // Other impairments
      right_amputation_percent: number | null;
      left_amputation_percent: number | null;
      right_triggering: string | null;
      left_triggering: string | null;
      right_extensor_subluxation_mcp: string | null;
      left_extensor_subluxation_mcp: string | null;
    };
    
    // Middle fingers data structure
    middle_fingers: {
      // DIP Joint
      right_dip_flexion: number | null;
      left_dip_flexion: number | null;
      right_dip_extension: number | null;
      left_dip_extension: number | null;
      // PIP Joint
      right_pip_flexion: number | null;
      left_pip_flexion: number | null;
      right_pip_extension: number | null;
      left_pip_extension: number | null;
      // MCP Joint
      right_mcp_flexion: number | null;
      left_mcp_flexion: number | null;
      right_mcp_extension: number | null;
      left_mcp_extension: number | null;
      // Sensory data
      right_radial_two_point: number | null;
      left_radial_two_point: number | null;
      right_radial_two_point_percent: number | null;
      left_radial_two_point_percent: number | null;
      right_ulnar_two_point: number | null;
      left_ulnar_two_point: number | null;
      right_ulnar_two_point_percent: number | null;
      left_ulnar_two_point_percent: number | null;
      // Other impairments
      right_amputation_percent: number | null;
      left_amputation_percent: number | null;
      right_triggering: string | null;
      left_triggering: string | null;
      right_extensor_subluxation_mcp: string | null;
      left_extensor_subluxation_mcp: string | null;
    };
    
    // Ring fingers data structure
    ring_fingers: {
      // DIP Joint
      right_dip_flexion: number | null;
      left_dip_flexion: number | null;
      right_dip_extension: number | null;
      left_dip_extension: number | null;
      // PIP Joint
      right_pip_flexion: number | null;
      left_pip_flexion: number | null;
      right_pip_extension: number | null;
      left_pip_extension: number | null;
      // MCP Joint
      right_mcp_flexion: number | null;
      left_mcp_flexion: number | null;
      right_mcp_extension: number | null;
      left_mcp_extension: number | null;
      // Sensory data
      right_radial_two_point: number | null;
      left_radial_two_point: number | null;
      right_radial_two_point_percent: number | null;
      left_radial_two_point_percent: number | null;
      right_ulnar_two_point: number | null;
      left_ulnar_two_point: number | null;
      right_ulnar_two_point_percent: number | null;
      left_ulnar_two_point_percent: number | null;
      // Other impairments
      right_amputation_percent: number | null;
      left_amputation_percent: number | null;
      right_triggering: string | null;
      left_triggering: string | null;
      right_extensor_subluxation_mcp: string | null;
      left_extensor_subluxation_mcp: string | null;
    };
    
    // Little fingers data structure
    little_fingers: {
      // DIP Joint
      right_dip_flexion: number | null;
      left_dip_flexion: number | null;
      right_dip_extension: number | null;
      left_dip_extension: number | null;
      // PIP Joint
      right_pip_flexion: number | null;
      left_pip_flexion: number | null;
      right_pip_extension: number | null;
      left_pip_extension: number | null;
      // MCP Joint
      right_mcp_flexion: number | null;
      left_mcp_flexion: number | null;
      right_mcp_extension: number | null;
      left_mcp_extension: number | null;
      // Sensory data
      right_radial_two_point: number | null;
      left_radial_two_point: number | null;
      right_radial_two_point_percent: number | null;
      left_radial_two_point_percent: number | null;
      right_ulnar_two_point: number | null;
      left_ulnar_two_point: number | null;
      right_ulnar_two_point_percent: number | null;
      left_ulnar_two_point_percent: number | null;
      // Other impairments
      right_amputation_percent: number | null;
      left_amputation_percent: number | null;
      right_triggering: string | null;
      left_triggering: string | null;
      right_extensor_subluxation_mcp: string | null;
      left_extensor_subluxation_mcp: string | null;
    };
  };

  knees: {
    right_flexion: number | null;
    left_flexion: number | null;
    right_extension: number | null;
    left_extension: number | null;
    right_flexion_contracture_passive: number | null;
    left_flexion_contracture_passive: number | null;
    right_extensor_lag: number | null;
    left_extensor_lag: number | null;
    right_varus: number | null;
    left_varus: number | null;
    right_valgus: number | null;
    left_valgus: number | null;
    tka_applies_right: boolean | null;
    tka_applies_left: boolean | null;
    tka_right: TkaSideInput | null;
    tka_left: TkaSideInput | null;
  };
  ankles: {
    right_plantar_flexion: number | null;
    left_plantar_flexion: number | null;
    right_dorsiflexion: number | null;
    left_dorsiflexion: number | null;
    right_inversion: number | null;
    left_inversion: number | null;
    right_eversion: number | null;
    left_eversion: number | null;
    right_subtalar_inversion: number | null;
    left_subtalar_inversion: number | null;
    right_subtalar_eversion: number | null;
    left_subtalar_eversion: number | null;
  };
  gait: {
    description: string | null;
    antalgic_gait: boolean | null;
  };
  grip_strength: {
    limited_by: 'pain' | 'effort' | null;
    right_attempt_1: number | null;
    left_attempt_1: number | null;
    right_attempt_2: number | null;
    left_attempt_2: number | null;
    right_attempt_3: number | null;
    left_attempt_3: number | null;
  };
};

export const HAND_DIGITS = ['thumbs', 'index_fingers', 'middle_fingers', 'ring_fingers', 'little_fingers'] as const;

/**
 * "Fill Normal Values" on any digit fills the WHOLE hand. The digit the button
 * belongs to is set to normal outright (as before). The other four only get
 * normals in their blank fields, so a value already recorded there (e.g. an
 * abnormal finding the AI parsed) is never overwritten.
 */
export function fillHandNormals(hands: any, clickedDigit: string): any {
  const out: any = { ...(hands || {}) };
  for (const digit of HAND_DIGITS) {
    const normals = (NORMAL_ROM_VALUES as any)[digit] || {};
    const current = out[digit] || {};
    if (digit === clickedDigit) {
      out[digit] = { ...current, ...normals };
    } else {
      const filled: any = { ...current };
      for (const [k, v] of Object.entries(normals)) {
        if (filled[k] === null || filled[k] === undefined || filled[k] === '') filled[k] = v;
      }
      out[digit] = filled;
    }
  }
  return out;
}

/**
 * Whether anything at all is recorded for one side of the hand: any field of any
 * digit, not just motion. The Hands tab used to look only at thumb motion and
 * finger flexion, so triggering, amputation, sensory loss or an extension lag on
 * their own showed "--" while the copied report rated them.
 */
export function handSideRecorded(hands: any, side: 'right' | 'left'): boolean {
  if (!hands) return false;
  return HAND_DIGITS.some((digit) => {
    const d = hands[digit];
    return !!d && Object.entries(d).some(([k, v]) => k.startsWith(`${side}_`) && v !== null && v !== undefined && v !== '');
  });
}
